import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import { useRef, useEffect } from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import type { FileRouteTypes } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import { Chart, PatientDoc, OptionVideos, TreatmentOptions, TreatmentOptionsType, handleAutomaticTreatmentOptionUpdateProps } from '@usacsmideo/types';
import useChart from '../../../../hooks/useChart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { Heading } from '../../../../components/Heading';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { useInitChartSection } from '../../../../hooks/useInitChartSection';
import { notifyChartSaveError } from '../../../../utilities/chartSaveError';
import { Button } from '../../../../components';
import { FieldInfo } from '../../../../components/FieldInfo';
import { FormSelect } from '../../../../components/forms/FormSelect';
import { getUnattestedScript } from '../../../../data/treatment/getUnattestedScript';
import { ScriptInput } from '../../../../components/forms/ScriptInput';
import * as Sentry from '@sentry/browser';
import { classNames } from '../../../../style/classNames';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
import { useVideoProgressTracking } from '../../../../hooks/useVideoProgressTracking';

export const Route = createFileRoute('/_authenticated/$patientId/education/treatmentOption')({
  component: TreatmentOptionPage,
});

function TreatmentOptionPage() {
  const { mutate, mutateAsync } = useUpdateChartSection();
  const { patientId } = Route.useParams();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const treatmentCodes = Object.keys(TreatmentOptions);
  const treatmentOptionsArray = Object.keys(TreatmentOptions).map((label, i) => ({
    name: i + 1 + ' - ' + TreatmentOptions[label as TreatmentOptionsType],
    value: label as TreatmentOptionsType,
  }));
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentLocation = routerState.location;
  const pathname = currentLocation.pathname;

  // Video progress tracking
  const selfGuidedVideoRef = useRef<HTMLVideoElement>(null);
  const conciergeVideoRef = useRef<HTMLVideoElement>(null);

  // Self-guided always uses fullCode
  const selfGuidedVideoId = 'treatmentOption_fullCode';
  const { fullyWatched: selfGuidedFullyWatched, isReady: selfGuidedIsReady } = useVideoProgressTracking({
    patientId,
    videoId: selfGuidedVideoId,
    videoRef: selfGuidedVideoRef,
  });

  // Concierge uses the selected treatment code
  const conciergeVideoId = chart?.SELECTED_TREATMENT_OPTION?.code
    ? `treatmentOption_${chart.SELECTED_TREATMENT_OPTION.code}`
    : 'treatmentOption_fullCode';
  // Track concierge progress but don't gate the Next button on it
  useVideoProgressTracking({
    patientId,
    videoId: conciergeVideoId,
    videoRef: conciergeVideoRef,
  });

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfo,
      chart: chart,
    });

    if ('incompleteMessage' in res) {
      // THE PATIENT HAS NOT COMPLETED THE NECESSARY SCREENS
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      // FIRESTORE ERROR
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    // NAVIGATION COMPLETE — PERSIST FINAL STATE AND NAVIGATE HOME
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/' });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }


    mutatePatientDoc(
      {
        patientId: patientId,
        navObj: res.updatedNavObj,
      },
      {
        onSuccess: () => {
          // NAVIGATE TO THE NEXT SCREEN
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error: Error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  // INITIALIZE FORM
  const treatmentOptionForm = useForm({
    defaultValues: {
      code: chart?.SELECTED_TREATMENT_OPTION?.code ? chart.SELECTED_TREATMENT_OPTION.code : 'fullCode',
      description: chart?.SELECTED_TREATMENT_OPTION?.description
        ? chart.SELECTED_TREATMENT_OPTION.description
        : TreatmentOptions.fullCode,
      unAttestedScript: chart?.SELECTED_TREATMENT_OPTION?.unAttestedScript
        ? chart.SELECTED_TREATMENT_OPTION.unAttestedScript
        : '',
    },
    validators: {
      onChange({ value }) {
        if (!treatmentCodes.includes(value.code)) {
          return { code: 'Please select a code' };
        }
        if (!value.description) {
          return { description: 'Please select a code' };
        }
        if (!value.unAttestedScript) {
          return { unAttestedScript: 'Script cannot be empty' };
        }
      },
    },
    onSubmit: () => {
      handleTreatmentOptionUpdate();
    },
  });

  const handleTreatmentOptionUpdate = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          SELECTED_TREATMENT_OPTION: {
            code: treatmentOptionForm.state.values.code,
            description: treatmentOptionForm.state.values.description,
            unAttestedScript: treatmentOptionForm.state.values.unAttestedScript,
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );

    mutate(
      {
        patientId: patientId,
        docData: {
          SCRIPT_REVIEW: {
            attestedCode: treatmentOptionForm.state.values.code,
            scriptAttested: false,
            speaker: chart.SCRIPT_REVIEW?.speaker ? chart.SCRIPT_REVIEW.speaker : 'patient',
            telePrompterScript: treatmentOptionForm.state.values.unAttestedScript,
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // RETURNS THE SELECTED_TREATMENT_OPTION WRITE, SO THE BACKGROUND INIT CAN LATCH ON IT. A PROMISE,
  // NOT A PER-CALL onError: THE SCRIPT_REVIEW mutate BELOW WOULD REPLACE THAT CALLBACK.
  const handleAutomaticTreatmentOptionUpdate = (payload: handleAutomaticTreatmentOptionUpdateProps) => {
    const selectedTreatmentOptionWrite = mutateAsync({
      patientId: patientId,
      docData: {
        SELECTED_TREATMENT_OPTION: {
          code: payload.code,
          description: payload.description,
          unAttestedScript: payload.unAttestedScript,
        },
      },
    });
    if (chart?.SCRIPT_REVIEW?.attestedCode) {
      mutate(
        {
          patientId: patientId,
          docData: {
            SCRIPT_REVIEW: {
              attestedCode: '',
              scriptAttested: false,
              telePrompterScript: '',
              speaker: chart.SCRIPT_REVIEW?.speaker ? chart.SCRIPT_REVIEW.speaker : 'patient',
            },
          },
        },
        {
          onSuccess: () => {
            // alert('Successfully updated document');
          },
          onError: () => {
            notifyChartSaveError();
          },
        },
      );
    }
    return selectedTreatmentOptionWrite;
  };

  const updateScript = (code: TreatmentOptionsType) => {
    // GENERATE SCRIPT
    const script = getUnattestedScript({ code, chart, patientInfo });

    // UPDATE FIRESTORE WITH SCRIPT - THIS WILL REFRESH THE SCRIPT COMPONENT
    const payload = {
      code: code,
      description: TreatmentOptions[code],
      unAttestedScript: script,
    };
    handleAutomaticTreatmentOptionUpdate(payload).catch(() => notifyChartSaveError());
  };

  // INITIALIZE DOCUMENT IF NONE EXISTS (OR IF CREATED EMPTY BY BACKEND) — ONCE PER FAILURE, NOT ONCE
  // PER SNAPSHOT. WAITS FOR patientInfo: getUnattestedScript DEREFERENCES patientInfo ITSELF (navType IS
  // OPTIONAL-CHAINED SINCE CU-17tm466qbce, BUT patientInfo IS NOT).
  useInitChartSection({
    needed: !!chart && !!patientInfo && !chart.SELECTED_TREATMENT_OPTION?.unAttestedScript,
    write: () =>
      handleAutomaticTreatmentOptionUpdate({
        code: 'fullCode',
        description: TreatmentOptions.fullCode,
        unAttestedScript: getUnattestedScript({ code: 'fullCode', chart, patientInfo }),
      }),
  });

  // SET UNATTESTED SCRIPT IF IT EXISTS IN THE CHART
  useEffect(() => {
    if (chart?.SELECTED_TREATMENT_OPTION?.unAttestedScript) {
      treatmentOptionForm.setFieldValue('unAttestedScript', chart.SELECTED_TREATMENT_OPTION.unAttestedScript);
    }
  }, [chart, treatmentOptionForm]);

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }

  // SELF-GUIDED PATIENTS ONLY EVER SEE FULL CODE VIDEO
  if (currentUser?.uid === patientId && patientInfo.navType?.base === 'self') {
    return (
      <>
        <div id="treatment-option-page" className="flex flex-col items-center justify-center">
          <Heading id="treatment-option-page-header" textContent="Code-Specific Educational Video" />
          <p className="text-xl">
            Below is a video that summarizes the the treatment options you are electing to receive.
          </p>
          <div id="treatment-option-video-container" className="mt-4 flex flex-col items-center">
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video ref={selfGuidedVideoRef} id={'full-code'} className="w-10/12" controls>
              <source src={OptionVideos['fullCode']} type="video/mp4" />
            </video>
          </div>
          {!selfGuidedIsReady && (
            <div className="mt-4">
              <p className="text-gray-500">Loading video progress...</p>
            </div>
          )}
          {selfGuidedIsReady && !selfGuidedFullyWatched && (
            <div className="mt-4">
              <p className="text-gray-600">Please watch the video to continue</p>
            </div>
          )}
          {selfGuidedFullyWatched && (
            <div id="nav" className="mt-4 flex flex-row">
              <div className="flex flex-col items-center">
                <button
                  id="submit-form-info"
                  title="Submit Form Info"
                  onClick={handleNextButtonClick}
                  disabled={false}
                  className={classNames.nextButton}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  // CONCIERGE-NAVIGATED PATIENTS SEE SPECIFIC CODE VIDEO, ONCE ASSIGNED
  if (patientInfo.navType?.base === 'concierge') {
    //
    return (
      <div className="">
        <div className="flex flex-col items-center">
          <p className="w-11/12 text-xl">
            Please wait for your concierge to assign a code status or select the code discussed with your medical
            provider below.
          </p>
        </div>
        <treatmentOptionForm.Field
          name="code"
          children={(field) => (
            <>
              <FormSelect
                label="Code"
                name="code"
                id="code-select"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={async (e) => {
                  updateScript(e.target.value as TreatmentOptionsType);
                  field.handleChange(e.target.value as TreatmentOptionsType);
                }}
                required={true}
                options={treatmentOptionsArray}
              />
              <FieldInfo fieldMeta={field.state.meta} />
            </>
          )}
        />
        <treatmentOptionForm.Field
          name="unAttestedScript"
          validators={{
            onChange({ value }) {
              if (!value) {
                return 'Script cannot be empty';
              } else {
                return undefined;
              }
            },
          }}
          children={(field) => (
            <>
              <ScriptInput
                label="Script Preview"
                id="script-input"
                type="textarea"
                name={field.name}
                value={field.state.value}
                handleBlur={field.handleBlur}
                handleChange={(e) => field.handleChange(e.target.value)}
                required={true}
              />
              <FieldInfo fieldMeta={field.state.meta} />
            </>
          )}
        />
        <div id="treatment-option-page-video" className="flex flex-col items-center justify-center">
          {chart.SELECTED_TREATMENT_OPTION?.code && (
            /* eslint-disable-next-line jsx-a11y/media-has-caption */
            <video
              ref={conciergeVideoRef}
              src={OptionVideos[chart.SELECTED_TREATMENT_OPTION.code]}
              id={chart.SELECTED_TREATMENT_OPTION.code}
              className="w-full"
              controls
            />
          )}
        </div>
        <div className="mt-8 flex flex-col items-center">
          <treatmentOptionForm.Subscribe
            selector={(state) => [state.canSubmit, state.errors]}
            children={([canSubmit]) => (
              <>
                {!canSubmit && (
                  <div className="mt-4 flex flex-col items-center">
                    <p className="text-red-500">Please correct any errors above before re-submitting.</p>
                  </div>
                )}
                <Button
                  id="submit-hca-info"
                  type="submit"
                  disabled={!canSubmit}
                  onClick={treatmentOptionForm.handleSubmit}
                  textContent="Submit"
                />
              </>
            )}
          />
        </div>
        {chart.SELECTED_TREATMENT_OPTION?.unAttestedScript && (
          <div className="flex flex-col items-center">
            <div className="flex flex-col items-center">
              <button
                id="submit-form-info"
                title="Submit Form Info"
                onClick={handleNextButtonClick}
                disabled={false}
                className={classNames.nextButton}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return <div>Loading...</div>;
}
